/** @jsxImportSource @emotion/react */
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Redirect } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';

import { recommendationsMapPageAnalytics } from '../../analyticsConsts';
import {
  CLUB,
  CLUB_CASUAL,
  KICKABOUT_CASUAL,
  MAP_PAGE,
  PAGE_NUMBER,
  PAGE_SIZE,
  RECOMMENDATIONS_PAGE_TITLE_MAPPING,
  SELECTED_FOOTBALL_TYPE_ID,
} from '../../const';
import { usePreferencesContext } from '../../context/preferences';
import { useRecommendations } from '../../context/recommendations';
import { useSetHeaderHeightContext } from '../../context/setHeaderHeight';
import useBodyClass from '../../hooks/useBodyClass';
import useErrorBoundary from '../../hooks/useErrorBoundary';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePageLoadInfo from '../../hooks/usePageLoadInfo';
import trayStateReducer, {
  EXPAND_ACTION,
  EXPAND_MAP_ACTION,
  SHRINK_ACTION,
  STANDARD,
} from '../../reducers/trayState';
import { getRecommendations } from '../../services';
import normaliseRecommendations from '../../utils/normaliseRecommendations';
import CardListView from '../CardListView';
import CFADivider from '../CFADivider';
import CFAOpenFiltersMSG from '../CFAOpenFiltersMSG';
import FilterListMapView from '../FilterListMapView';
import FiltersMenu from '../FiltersMenu';
import Loader from '../Loader';
import LoadMoreResultsButton from '../LoadMoreResultsButton';
import NumOfOpportunities from '../NumOfOpportunities';
import ProvidersMap from '../ProvidersMap';
import useStyles from './styles';

const { RECOMMENDATIONS_RESULT_ON_MAP } = recommendationsMapPageAnalytics;

const SHOWN_INCREMENT = 12;
const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'scroll', 'overlay'];

const getRecommendationId = item => item.ClubId || item.CentreId;

const getMapPinId = (item, index) =>
  `${getRecommendationId(item) || 'recommendation'}-${index}`;

const getScrollableAncestor = element => {
  let parent = element?.parentElement;
  const { body, defaultView } = element?.ownerDocument || {};

  while (parent && parent !== body) {
    const style = defaultView?.getComputedStyle(parent);
    const canScrollY =
      SCROLLABLE_OVERFLOW_VALUES.includes(style?.overflowY) &&
      parent.scrollHeight > parent.clientHeight;
    const canScrollX =
      SCROLLABLE_OVERFLOW_VALUES.includes(style?.overflowX) &&
      parent.scrollWidth > parent.clientWidth;

    if (canScrollY || canScrollX) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

const MapPage = () => {
  useBodyClass('mapPage', '/recommendations/map');
  const isMobile = useMediaQuery(`screen and (max-width: 1023px)`);
  const styles = useStyles();
  const [filterShowHide, setFilterShowHide] = useState(false);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const throwError = useErrorBoundary();
  const { preferences } = usePreferencesContext();
  const { FootballType, [SELECTED_FOOTBALL_TYPE_ID]: selectedFootballType } =
    preferences || {};
  const { headerHeight } = useSetHeaderHeightContext();

  const {
    clubRecommendations,
    casualRecommendations,
    clubCasualRecommendations,
    setRecommendations,
  } = useRecommendations();

  const [visibleCount, setVisibleCount] = useState(SHOWN_INCREMENT);
  // ✅ ADDED: Ref for focusing the first new card
  const firstNewCardRef = useRef(null);
  const loadingRefFilter = useRef();
  let recommendationsType = null;
  if (selectedFootballType === CLUB) {
    recommendationsType = clubRecommendations;
  } else if (selectedFootballType === KICKABOUT_CASUAL) {
    recommendationsType = casualRecommendations;
  } else if (selectedFootballType === CLUB_CASUAL) {
    recommendationsType = clubCasualRecommendations;
  }

  const totalRecommendations = recommendationsType?.CntOpportunity;
  const recommendations = useMemo(() => {
    if (!recommendationsType?.RecommendationClubCentreCartDto) {
      return null;
    }

    // we need to normalise the recommendations to make sure we offset
    // pins that are at the same location
    // sorting cards based on distance
    return normaliseRecommendations(
      recommendationsType?.RecommendationClubCentreCartDto,
    )?.sort((firstDistance, secondDistance) =>
      firstDistance.DistanceFromUserLocation >
        secondDistance.DistanceFromUserLocation
        ? 1
        : firstDistance.DistanceFromUserLocation <
          secondDistance.DistanceFromUserLocation
          ? -1
          : 0,
    );
  }, [recommendationsType?.RecommendationClubCentreCartDto]);

  const [trayState, dispatch] = useReducer(trayStateReducer, STANDARD);
  const handlers = useSwipeable({
    onSwipedUp: () => dispatch(EXPAND_ACTION),
    onSwipedDown: () => dispatch(SHRINK_ACTION),
    preventDefaultTouchmoveEvent: true,
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef();

  const cardRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shouldScrollToNewCard, setShouldScrollToNewCard] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const isListView = !selectedId;
  const providerCardsRef = useRef(null);
  // const visibleCardsForDivider = recommendations.slice(0, visibleCount) || [];
  const visibleCardsForDivider = useMemo(() => {
    return (
      recommendations?.slice(0, visibleCount).map((item, index) => ({
        ...item,
        MapPinId: getMapPinId(item, index),
      })) || []
    );
  }, [recommendations, visibleCount]);

  const idToIndexMap = useMemo(() => {
    const map = {};

    visibleCardsForDivider.forEach((item, index) => {
      const id = item.MapPinId;
      map[id] = index;
    });

    return map;
  }, [visibleCardsForDivider]);

  const scrollToCard = useCallback(
    id => {
      const index = idToIndexMap[id];
      if (index === undefined) return;

      const element = cardRefs.current[index];

      if (element) {
        const scrollContainer = getScrollableAncestor(element);

        if (!scrollContainer) {
          return;
        }

        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const nextTop =
          scrollContainer.scrollTop + elementRect.top - containerRect.top;
        const nextLeft =
          scrollContainer.scrollLeft + elementRect.left - containerRect.left;

        scrollContainer.scrollTo({
          top: nextTop,
          left: nextLeft,
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    },
    [idToIndexMap],
  );

  // set the page title.
  usePageLoadInfo('map', `${RECOMMENDATIONS_PAGE_TITLE_MAPPING[FootballType]}`);

  // If the number of cards shown changes we want to
  // move the focus to the first new card
  useLayoutEffect(() => {
    if (!shouldScrollToNewCard) return;
    if (visibleCount <= SHOWN_INCREMENT) return;

    const element = firstNewCardRef.current;
    if (element) {
      element.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'start',
      });
      element.focus({ preventScroll: true });
    }
    setShouldScrollToNewCard(false);
  }, [visibleCount, shouldScrollToNewCard, visibleCardsForDivider]);

  const handleIntersection = useCallback(
    entry => {
      if (!entry.isIntersecting) {
        return;
      }
      const index = Number(entry.target.dataset.index);
      setActiveIndex(index);
      //automatically select the pin when the card is in view
      if (isMobile) {
        const card = visibleCardsForDivider[index];
        if (card) {
          const id = card.MapPinId;
          setSelectedId(id);
        }
      }
    }, [visibleCardsForDivider, isMobile]);

  const handleIntersections = useCallback(
    entries => {
      entries.forEach(handleIntersection);
    }, [handleIntersection]);

  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      const observer = new IntersectionObserver(handleIntersections, {
        root: providerCardsRef.current, // scroll container
        threshold: 0.7, // 70% visible → treat as active
      });

      cardRefs.current.forEach(card => {
        if (card) observer.observe(card);
      });
      return () => observer.disconnect();
    });

    return () => cancelAnimationFrame(timer);
  }, [visibleCount, handleIntersections]);

  const totalCards = visibleCardsForDivider.length;
  const DOTS = 7;

  const activeDot = Math.min(
    DOTS - 1,
    Math.floor((activeIndex / (totalCards - 1)) * DOTS),
  );

  // set path information for redirect
  useEffect(() => {
    localStorage.setItem('pathInfomation', window.location.pathname);
  }, []);

  // If the user loads more recommendations we want to move the focus to
  // the loading element
  useLayoutEffect(() => {
    setTimeout(() => loadingRef.current?.focus(), 200);
  }, [isLoadingMore]);

  useEffect(() => {
    if (isLoadingFilter) {
      loadingRefFilter.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isLoadingFilter, loadingRefFilter]);

  if (!selectedFootballType) {
    return <Redirect to="/" />;
  }

  const showNext = () => {
    setShouldScrollToNewCard(true);
    setVisibleCount(visibleCount + SHOWN_INCREMENT);
  };

  const getMoreResults = () => {
    if (!recommendations) {
      return;
    }
    setIsLoadingMore(true);
    getRecommendations(selectedFootballType, {
      ...preferences,
      // get the page after the current one
      [PAGE_NUMBER]: Math.floor(visibleCount / preferences[PAGE_SIZE]) + 1,
    })
      //])
      .then(({ data }) => {
        if (data) {
          const recommendationRecords = data; // recommendation data
          const updatedRecommendations = {
            ...recommendations,
            RecommendationClubCentreCartDto: [
              ...(recommendationsType?.RecommendationClubCentreCartDto ?? []),
              ...(recommendationRecords.RecommendationClubCentreCartDto ?? []),
            ],
            CntOpportunity:
              recommendationRecords.CntOpportunity ||
              recommendations.CntOpportunity,
          };
          setRecommendations(updatedRecommendations, selectedFootballType);
          setIsLoadingMore(false);
          showNext();
        }
      })
      .catch(throwError);
  };

  if (!recommendations) {
    return <p>No recommendations match the current filters</p>;
  }
  const resetVisibleCount = () => {
    setVisibleCount(SHOWN_INCREMENT);
  }
  return (
    <div css={styles.topSpacer(headerHeight)}>
      <FilterListMapView
        filterShowHide={filterShowHide}
        setFilterShowHide={setFilterShowHide}
        currentView="list"
      />
      <FiltersMenu
        filterShowHide={filterShowHide}
        page={MAP_PAGE}
        setFilterShowHide={setFilterShowHide}
        onLoadingChange={setIsLoadingFilter}
        onResetVisibleCount={resetVisibleCount}
        shownIncrement={SHOWN_INCREMENT}
      />
      {isLoadingFilter ? (
        <Loader
          message="Loading results"
          ref={loadingRefFilter}
          isOnboarding="true"
          isFilter="true"
          tabIndex="-1"
          css={styles.loader}
        />
      ) : (
        <div css={styles.base(isListView)}>
          <div css={styles.mapAreaWrapper(headerHeight)}>
            <ProvidersMap
              data={visibleCardsForDivider}
              handleSelect={setSelectedId}
              selectedId={selectedId}
              scrollToCard={scrollToCard}
              userLocation={{
                latitude: preferences.Latitude,
                longitude: preferences.Longitude,
              }}
              handleMapMovement={() => dispatch(EXPAND_MAP_ACTION)}
            />
          </div>
          <div
            css={styles.providerCardWrapper(
              isListView,
              trayState,
              headerHeight,
            )}
            id={RECOMMENDATIONS_RESULT_ON_MAP}
          >
            {totalRecommendations > 0 && (
              <>
                <div
                  {...handlers}
                  css={styles.totalResults(isListView)}
                  className="resultsOpportunity"
                >
                  <span>
                    <NumOfOpportunities
                      numOfOpportunities={totalRecommendations}
                      isMapPage
                    />
                  </span>
                </div>
                <div
                  css={styles.scrollContainer(isListView, trayState)}
                  className="observerRoot"
                >
                  {visibleCardsForDivider?.length > 0 && (
                    <div
                      css={styles.providerCards(isListView)}
                      ref={providerCardsRef}
                    >
                      {visibleCardsForDivider.map((item, index) => {
                        const id = item.MapPinId;
                        // ✅ ADDED: Determine if this is the first card of the new batch
                        const isFirstNewCard =
                          index === visibleCount - SHOWN_INCREMENT;
                        return (
                          <Fragment key={id}>
                            <div
                              ref={el => (cardRefs.current[index] = el)}
                              data-index={index}
                              css={styles.cardContainer(isMobile)}
                            >
                              <CardListView
                                index={index}
                                cardType={
                                  item.FootballType === CLUB
                                    ? 'club'
                                    : 'sessional'
                                }
                                // ✅ ADDED: Conditionally apply the ref
                                ref={isFirstNewCard ? firstNewCardRef : null}
                                // isLink={false}
                                isLink={isMobile}
                                isSelected={selectedId === id}
                                onClick={() => setSelectedId(id)}
                                {...item}
                              />
                            </div>
                            {!isMobile && (index + 1) % 24 === 0 && (
                              <CFADivider
                                filterShowHide={filterShowHide}
                                isMapPage
                              />
                            )}
                          </Fragment>
                        );
                      })}
                      {isMobile && (
                        <div css={styles.loadMoreCardContainer}>
                          {totalRecommendations > 0 && (
                            <div css={styles.btnWrapper}>
                              {isLoadingMore ? (
                                <Loader
                                  inline
                                  message={`Loading more results`}
                                  css={styles.loadMore}
                                  ref={loadingRef}
                                />
                              ) : (
                                visibleCount < totalRecommendations && (
                                  <LoadMoreResultsButton
                                    onClick={getMoreResults}
                                    isMapPage
                                    isMobile
                                  />
                                )
                              )}
                            </div>
                          )}
                          <CFADivider
                            filterShowHide={filterShowHide}
                            isMapPage
                            isMobile
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {!isMobile && totalRecommendations > 0 && (
                    <div css={styles.btnWrapper}>
                      {isLoadingMore ? (
                        <Loader
                          inline
                          message={`Loading more results`}
                          css={styles.loadMore}
                          ref={loadingRef}
                        />
                      ) : (
                        visibleCount < totalRecommendations && (
                          <LoadMoreResultsButton
                            onClick={getMoreResults}
                            isMapPage
                          />
                        )
                      )}
                    </div>
                  )}
                  {isMobile && (
                    <div css={styles.dotsContainer}>
                      {Array.from({ length: DOTS }).map((_, i) => (
                        <div key={i} css={styles.dots(i, activeDot)} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {totalRecommendations <= 0 && (
              <div css={styles.noResultsWrapper}>
                <div {...handlers} css={styles.totalResults(isListView)}>
                  <span>
                    <NumOfOpportunities
                      numOfOpportunities={totalRecommendations}
                      isMapPage
                    />
                  </span>
                </div>
                <div css={styles.noOppOpenFilterDiv(filterShowHide)}>
                  <CFAOpenFiltersMSG
                    filterShowHide={filterShowHide}
                    setFilterShowHide={setFilterShowHide}
                    isMapPage
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapPage;
